import { h } from '@omni-torn/ui';
import { changeSince, flattenPersonalStats, seriesCsv, type Snapshot } from '@omni-torn/shared';
import { theirStatsWithMyKey } from '../player-stats';
import { history, server, session, torn } from '../session';
import { KEY_STATS, psGroup, psLabel, SECONDS_STATS } from '../stat-names';
import { box, downloadText, ext, kids, lineChart, loading, num, problem, profileUrl, signed, tabs, withTableCsv } from '../ui';
import { historyNote } from './shared';

const DAY = 86_400_000;
type Period = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'all';
const PERIODS: Array<{ id: Period; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: '7 days' },
  { id: 'month', label: '30 days' },
  { id: 'quarter', label: '90 days' },
  { id: 'year', label: '1 year' },
  { id: 'all', label: 'All time' },
];
const PERIOD_DAYS: Record<Exclude<Period, 'today' | 'all'>, number> = { week: 7, month: 30, quarter: 90, year: 365 };
/** Chart ranges (days; null = everything kept, 400 days). */
const RANGES: Array<{ id: string; label: string; days: number }> = [
  { id: '7', label: '7 days', days: 7 },
  { id: '30', label: '30 days', days: 30 },
  { id: '90', label: '90 days', days: 90 },
  { id: '365', label: '1 year', days: 365 },
  { id: 'all', label: 'All', days: 400 },
];

/** Start of a period (ms): today = since 00:00 TCT; "all" = the start of time (the oldest kept snapshot is used). */
export function periodStart(p: Period, now = Date.now()): number {
  if (p === 'today') return Math.floor(now / DAY) * DAY;
  if (p === 'all') return 0;
  return now - PERIOD_DAYS[p] * DAY;
}

const show = (k: string, v: number | null) => (v === null ? '—' : SECONDS_STATS.has(k) ? `${num(v / 3600, 1)} h` : num(v));

export async function renderStats(view: HTMLElement): Promise<void> {
  const body = h('div', {}, loading('Reading your personal stats…'));
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Personal stats')), body);
  let now: Record<string, number>;
  try {
    const r = (await torn('/user/', { selections: 'personalstats' })) as { personalstats?: unknown };
    now = flattenPersonalStats(r.personalstats);
  } catch (err) {
    body.replaceChildren(problem(err));
    return;
  }
  const me = session.me();
  const historyOn = me?.prefs.history !== false;
  let snaps: Snapshot[] = [];
  if (historyOn) snaps = await history(31).catch(() => []);
  /** Longer periods: the one kept snapshot each starts from (asked once each). */
  const bases = new Map<Period, Snapshot[]>();
  const fromTorn = new Map<Period, Record<string, number>>();
  let period: Period = 'week';
  let filter = '';
  let chartKey: string | null = null;
  let chartRange = '30';
  /** Another player's public personal stats (Compare), shown beside yours — never kept anywhere. */
  let other: { id: number; name: string; ps: Record<string, number> } | null = null;

  const baseFor = (p: Period): Snapshot[] => (p === 'today' || p === 'week' || p === 'month' ? snaps : (bases.get(p) ?? []));
  const sinceFor = (p: Period): number => (p === 'all' ? (bases.get('all')?.[0]?.at ?? 0) : periodStart(p));
  const change = (k: string): number | null => {
    const list = baseFor(period);
    const mine = list.length ? changeSince(list, (s) => s.ps?.[k], sinceFor(period), now[k]) : null;
    if (mine !== null) return mine;
    const past = fromTorn.get(period);
    return past && past[k] !== undefined && now[k] !== undefined ? now[k]! - past[k]! : null;
  };

  const tiles = h('div', { class: 'tiles' });
  const groups = h('div', {});
  const chartBox = h('div', {});
  const compareNote = h('div', {});

  const series = new Map<string, Array<{ at: number; v: number }>>();
  const drawChart = async () => {
    if (!chartKey) return chartBox.replaceChildren();
    const k = chartKey;
    const days = RANGES.find((r) => r.id === chartRange)?.days ?? 30;
    const id = `${k}:${days}`;
    const holder = h('div', {}, loading());
    const csv = h('button', { type: 'button', class: 'btn-small', onclick: () => downloadText(`${k}-${days}d.csv`, seriesCsv(psLabel(k), series.get(id) ?? [])) }, 'Export CSV');
    chartBox.replaceChildren(
      box(
        `${psLabel(k)} over time`,
        [
          tabs(
            RANGES.map((r) => ({ id: r.id, label: r.label })),
            chartRange,
            (r) => {
              chartRange = r;
              void drawChart();
            },
          ),
          holder,
        ],
        { id: 'ps-chart', extra: h('span', { class: 'head-actions' }, csv, closeBtn(() => ((chartKey = null), void drawChart()))) },
      ),
    );
    if (!series.has(id)) {
      try {
        series.set(id, (await server<{ points: Array<{ at: number; v: number }> }>('GET', `/api/me/history?days=${days}&stat=ps.${encodeURIComponent(k)}`)).points);
      } catch (err) {
        holder.replaceChildren(problem(err));
        return;
      }
    }
    if (chartKey !== k) return;
    holder.replaceChildren(lineChart(series.get(id) ?? [], { label: psLabel(k) }));
  };
  const openChart = (k: string) => {
    chartKey = k;
    void drawChart();
    chartBox.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  };

  const draw = () => {
    tiles.replaceChildren(
      ...KEY_STATS.filter((k) => now[k] !== undefined).map((k) => {
        const c = change(k);
        return h('button', { type: 'button', class: 'tile', title: historyOn ? 'Show the chart' : psLabel(k), onclick: () => openChart(k) }, h('div', { class: 'tile-label' }, psLabel(k)), h('div', { class: 'tile-value' }, show(k, now[k]!)), h('div', { class: `tile-change ${c && c > 0 ? 't-green' : ''}` }, c === null ? '' : signed(c, (x) => show(k, x))));
      }),
    );
    const byGroup = new Map<string, string[]>();
    const keys = new Set([...Object.keys(now), ...(other ? Object.keys(other.ps) : [])]);
    for (const k of [...keys].sort((a, b) => psLabel(a).localeCompare(psLabel(b)))) {
      if (filter && !psLabel(k).toLowerCase().includes(filter) && !k.includes(filter)) continue;
      const g = psGroup(k);
      byGroup.set(g, [...(byGroup.get(g) ?? []), k]);
    }
    const order = ['Attacking', 'Crimes & jail', 'Medical', 'Drugs & boosters', 'Travel', 'Items & trading', 'Racing & skills', 'Activity', 'Other'];
    const periodLabel = PERIODS.find((p) => p.id === period)!.label;
    groups.replaceChildren(
      ...order
        .filter((g) => byGroup.has(g))
        .map((g) =>
          h(
            'details',
            { class: 'group', open: !!filter || !!other || g === 'Attacking' || g === 'Drugs & boosters' },
            h('summary', {}, g, h('span', { class: 'muted' }, ` (${byGroup.get(g)!.length})`)),
            withTableCsv(h(
              'div',
              { class: 'table-wrap' },
              h(
                'table',
                { class: 'stat-table' },
                h('thead', {}, h('tr', {}, h('th', {}, 'Stat'), h('th', { class: 'r' }, 'Now'), h('th', { class: 'r' }, periodLabel), ...(other ? [h('th', { class: 'r' }, other.name), h('th', { class: 'r', title: 'You minus them' }, 'Difference')] : []))),
                h(
                  'tbody',
                  {},
                  ...byGroup.get(g)!.map((k) => {
                    const c = change(k);
                    const mine = now[k] ?? null;
                    const theirs = other?.ps[k] ?? null;
                    const diff = mine !== null && theirs !== null ? mine - theirs : null;
                    const row = h(
                      'tr',
                      { class: historyOn ? 'clickable' : '' },
                      h('td', {}, psLabel(k)),
                      h('td', { class: 'r' }, show(k, mine)),
                      h('td', { class: `r ${c && c > 0 ? 't-green' : c && c < 0 ? 't-red' : 'muted'}` }, c === null ? '—' : signed(c, (x) => show(k, x))),
                      ...(other ? [h('td', { class: 'r', 'data-them': k }, show(k, theirs)), h('td', { class: `r ${diff && diff > 0 ? 't-green' : diff && diff < 0 ? 't-red' : 'muted'}` }, diff === null ? '—' : signed(diff, (x) => show(k, x)))] : []),
                    );
                    if (historyOn && mine !== null) row.addEventListener('click', () => openChart(k));
                    return row;
                  }),
                ),
              ),
            )),
          ),
        ),
    );
  };

  const search = h('input', { type: 'search', placeholder: 'Find a stat…', class: 'filter', 'aria-label': 'Find a stat' }) as HTMLInputElement;
  search.addEventListener('input', () => ((filter = search.value.trim().toLowerCase()), draw()));

  const compare = async () => {
    const p = period;
    // Longer than the month the page reads at first: the one snapshot that period starts from.
    if (historyOn && !baseFor(p).length && (p === 'quarter' || p === 'year' || p === 'all') && !bases.has(p)) {
      bases.set(p, []);
      try {
        bases.set(p, (await server<{ history: Snapshot[] }>('GET', `/api/me/history?asof=${periodStart(p)}`)).history);
      } catch {
        bases.delete(p);
      }
      if (period === p) draw();
    }
    if (p === 'all') {
      compareNote.replaceChildren(bases.get('all')?.[0] ? h('p', { class: 'muted small' }, `All time = since ${new Date(bases.get('all')![0]!.at).toLocaleDateString()}, the oldest snapshot Omni-Torn keeps for you.`) : h('p', { class: 'muted small' }, 'No history kept yet.'));
      return;
    }
    // No Omni-Torn history that old: Torn can say what 10 stats were on a past day (one call per period).
    const list = baseFor(p);
    if (fromTorn.has(p) || (list[0] && list[0].at <= periodStart(p) + DAY)) return compareNote.replaceChildren();
    compareNote.replaceChildren(h('p', { class: 'muted' }, 'Asking Torn what they were…'));
    try {
      const r = (await torn('/user/', { selections: 'personalstats', stat: KEY_STATS.join(','), timestamp: String(Math.floor(periodStart(p) / 1000)) }, 3_600_000)) as { personalstats?: unknown };
      fromTorn.set(p, flattenPersonalStats(r.personalstats));
      compareNote.replaceChildren(h('p', { class: 'muted' }, 'Changes for the 10 main stats come from Torn’s own history. With Omni-Torn’s history on, every stat gets them (and charts).'));
    } catch (err) {
      compareNote.replaceChildren(problem(err));
    }
    draw();
  };

  // Compare with another player: their PUBLIC personal stats, one Torn call with your key on your click; not kept.
  const otherInput = h('input', { type: 'text', inputmode: 'numeric', placeholder: 'Player id', 'aria-label': 'Player id to compare with', maxlength: '9' }) as HTMLInputElement;
  const otherStatus = h('span', { class: 'muted small', 'data-compare': 'status' });
  const otherBtn = h('button', { type: 'submit', class: 'btn-small' }, 'Compare') as HTMLButtonElement;
  const clearBtn = h('button', { type: 'button', class: 'btn-small', hidden: true }, 'Clear') as HTMLButtonElement;
  const form = h('form', { class: 'compare-form', 'data-form': 'compare' }, otherInput, otherBtn, clearBtn, otherStatus);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const id = Number((/(\d{1,9})/.exec(otherInput.value) ?? [])[1]);
    if (!Number.isInteger(id) || id <= 0) {
      otherStatus.textContent = 'Type a player id (the number in their profile link).';
      return;
    }
    otherBtn.disabled = true;
    otherStatus.textContent = 'Asking Torn…';
    try {
      const t = await theirStatsWithMyKey(id);
      other = { id, name: t.name, ps: t.ps };
      otherStatus.replaceChildren('Comparing with ', ext(profileUrl(id), other.name), ' (their public personal stats) · ', h('a', { href: `/lookup/${id}`, 'data-link': true }, 'their player page'), '.');
      clearBtn.hidden = false;
      draw();
    } catch (err) {
      otherStatus.textContent = (err as Error).message;
    } finally {
      otherBtn.disabled = false;
    }
  });
  clearBtn.addEventListener('click', () => {
    other = null;
    clearBtn.hidden = true;
    otherStatus.textContent = '';
    draw();
  });

  body.replaceChildren(
    ...kids(
      historyNote(snaps.length),
      h(
        'div',
        { class: 'toolbar' },
        tabs(PERIODS, period, (id) => {
          period = id as Period;
          draw();
          void compare();
        }),
        search,
      ),
      compareNote,
      tiles,
      chartBox,
      box('Compare with another player', [form, h('p', { class: 'muted small' }, 'Their public personal stats, read by Torn with your key when you click (not kept). Click any of your rows for its chart.')], { id: 'compare' }),
      groups,
    ),
  );
  draw();
  void compare();
}

function closeBtn(f: () => void): HTMLElement {
  return h('button', { type: 'button', class: 'btn-small', onclick: f }, 'Close');
}
